import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Scrollbars from 'react-custom-scrollbars';
import { ButtonV2, DynamicTable, Icon, TooltipTrigger } from 'components';
import './style.scss';
import moment from 'moment';
import cx from 'classnames';
import useLaunchDarkly from 'hooks/useLaunchDarkly';
import { connect } from 'react-redux';

const GeneratedReports = ({ reports = [], currentUser = {} }) => {
  const flags = useLaunchDarkly();
  const formatDate = (date) => moment(date).format('Do MMM, YYYY [at] h:mm A');
  
  // normalizing reports data for Generated Reports table
  const normalized = useMemo(
    () =>
      (reports || []).map((r) => ({
        ...r,
        id: r.id || '',
        name: r.name || r.title || r.report_type || r.kind || '',
        generated_at: r.generated_at || null,
        generating: !r.generated_at && !r.failed,
        failed: Boolean(r.failed),
      })),
    [reports]
  );

  const sortedReports = useMemo(() => {
    return [...normalized]
      .sort((a, b) => {
        // generating reports first
        if (a.generating && !b.generating) return -1;
        if (b.generating && !a.generating) return 1;
        return 0;
      })
      .slice(0, 10);
  }, [normalized]);

  const reportOpened = async (report, format) => {
    if (!report) return;

    try {
      if (format === 'pdf' && report.pdf_url) {
        window.open(report.pdf_url, '_blank', 'noopener,noreferrer');
      } else if (format === 'xls' && report.xls_url) {
        const response = await fetch(report.xls_url);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = `${(report.name || 'report').replace(/\s+/g, '_')}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('reportOpened error', err);
    } finally {
      const payload = {
        ...report,
        id: report.id,
        opened_at: moment().format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
      };
      await req.markReportOpen(payload);
    }
  };

  const ListRow = ({ report }) => {
    const isFailed = Boolean(report.failed);
    const isGenerating = Boolean(report.generating);
    const isPrivate = report.is_private && flags['online-ent-privatereporting'];

    const iconCN = cx(
      'generated-reports__generated-item-icon',
      'generated-reports__generated-item-action',
      {
        'generated-reports__generated-item-icon--in-progress': !isFailed && isGenerating,
        'generated-reports__generated-item-icon--failed': isFailed,
      }
    );

    return (
      <div className="table__row generated-reports__item-container" data-testid={`generated_report_${report.id}`}>
        <div className="generated-reports__generated-item-left">
          {(!report.opened_at && currentUser.id === report.user_id) && !report.generating && !report.failed && (
            <Icon
              name="alert-circle"
              size={18}
              className="generated-reports__unopened-alert"
              aria-hidden
            />
          )}
          <div className="generated-reports__generated-item-name">{report.name}</div>
        </div>


        <div className="generated-reports__generated-item-actions" onClick={(e) => e.stopPropagation()}>
          {isGenerating && (
            <div className="generated-reports__generating-text-wrapper" aria-live="polite">
              <div className="generated-reports__generating-text" data-testid="generating-text">
                Generating...
              </div>
            </div>
          )}

          {isPrivate && (
            <TooltipTrigger tooltip="Private Report — only visible to you. Available for 24hrs." tooltipClassName="generated-reports__generated-item-tooltip" side="right">
              <ButtonV2 icon iconName="incognito" iconSize={28} className="generated-reports__generated-item-private" ariaLabel="Private report icon" />
            </TooltipTrigger>
          )}

          {isFailed ? (
            <ButtonV2 className={iconCN} icon iconName="alert" title="Failed" iconSize={28} ariaLabel="Failed" />
          ) : isGenerating ? (
            <ButtonV2 className={iconCN} icon iconName="pending-new" title="In Progress" iconSize={28} ariaLabel="In Progress" />
          ) : (
            <>
              {report.pdf_url && (
                <ButtonV2 icon iconName="pdf" iconSize={28} onClick={(e) => { e.preventDefault(); e.stopPropagation(); reportOpened(report, 'pdf'); }} ariaLabel="Open PDF" />
              )}

              {report.xls_url && report.pdf_url && <div className="generated-reports-vertical-rule" />}

              {report.xls_url && (
                <ButtonV2 icon iconName="csv" iconSize={28} onClick={(e) => { e.preventDefault(); e.stopPropagation(); reportOpened(report, 'xls'); }} ariaLabel="Download XLS" />
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="generated-reports" data-testid="generated-reports">
      <div className="modal__header"><div className="modal__header-title">Recently Generated Reports</div></div>

      <div className="modal__container">
        <div className="generated-reports__list-container">
          <Scrollbars style={{ height: '100%' }}>
            <DynamicTable fields={{ title: true, kind: true, status: true }} horizontalScroll={false} className="reg-list__list">
              <div className="table__header">
                <DynamicTable.Cell name="title" className="table__cell--s">Report Name</DynamicTable.Cell>
                <div className="table__cell table__cell--actions table__cell--xxs" />
              </div>

              <div className="table__body">
                {sortedReports.map((r) => <ListRow key={r.id} report={r} />)}
              </div>
            </DynamicTable>
          </Scrollbars>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  reports: state.reports.data || [],
  reportsLoading: state.reports.loading,
  currentUser: state.currentUser.data,
});

export default connect(mapStateToProps)(GeneratedReports);
